import MainLayout from './layouts/Main.vue';
import { Auth } from '@/capacitors/services/AuthService';
import { filter, switchMap, take } from 'rxjs/operators';
import * as VueRouter from 'vue-router';
import { isPlatform } from '@ionic/vue';

import { STAFF_KEY } from './stores/employee';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useAcl } from './stores/acl';
import { RouteNames } from './acl/permissions';
import { getPlatform } from './utils/misc';

export const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        // component: Home,
        redirect: () => {
          // Redirect based on platform
          if ((getPlatform() === 'desktop-web' || getPlatform() === 'mobile-web')) {
            return '/analytics';
          }
          return '/pos';
        },
      },
      {
        path: '/pos',
        name: 'pos',
        component: () => import('./views/pos/index.vue'),
        meta: {
          permission: RouteNames.POS,
        }
      },
      {
        path: '/table/:id',
        component: () => import('./views/pos/table/index.vue'),
        meta: {
          exitConfirmation: true,
        },
        name: "table"
      },
      {
        path: '/crm',
        component: () => import('@xpos/crm/src/components/crm/index.vue'),
      },
      {
        path: '/booking',
        name: 'booking',
        component: () => import('./views/booking/index.vue'),
        meta: { permission: RouteNames.BOOKING },
      },
      {
        path: '/management',
        component: () => import('./views/management/index.vue'),
        meta: {
          permission: RouteNames.MANAGEMENT,
        }
      },
      {
        path: '/settings',
        component: () => import('./views/settings/index.vue'),
        meta: {
          permission: RouteNames.SETTINGS,
        }
      },
      {
        path: '/reporting',
        component: () => import('./views/reporting/index.vue'),
        meta: { permission: RouteNames.REPORTING }
      },
      {
        path: '/analytics',
        component: () => import('./views/analytics/index.vue'),
        meta: { permission: RouteNames.ANALYTICS }
      }
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/login/index.vue'),
  },
  {
    path: '/authcallback',
    name: 'authcallback',
    component: () => import('./views/authCallback/index.vue'),
  },
  {
    path: '/endSession',
    name: 'endSession',
    component: () => import('./views/authCallback/EndSession.vue'),
  },
  {
    path: '/account',
    name: 'account',
    redirect: '/account/profile',
    component: () => import('./views/account/index.vue'),
    children: [
      {
        path: 'profile',
        name: 'profile',
        component: () => import('./views/account/components/profile.vue'),
      },
      {
        path: 'personal_data',
        name: 'personal_data',
        component: () => import('./views/account/components/personal_data.vue'),
      },
    ],
  },
];

const router = VueRouter.createRouter({
  history: isPlatform('electron') ? VueRouter.createWebHashHistory() : VueRouter.createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const isMobileWeb = getPlatform() === 'mobile-web';
  if (isMobileWeb && to.path !== '/') {
    const allowedRoutes = ['/analytics', '/reporting', '/login', '/authcallback', '/endSession', '/account'];
    const isAllowed = allowedRoutes.some(route => to.path.startsWith(route));
    if (!isAllowed) {
      next('/analytics');
      return;
    }
  }
  next();
});

router.beforeEach((to, from, next) => {
  if (to.name !== 'authcallback' && to.name !== 'login' && to.name !== 'profile' && to.name !== 'personal_data') {
    Auth.Instance.initComplete$
      .pipe(
        filter((complete) => complete),
        switchMap(() => Auth.Instance.isAuthenticated$),
        take(1),
      )
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          // No merchant selected
          if (secureStorage.getItem('merchant_uid')) {
            if (secureStorage.getItem(STAFF_KEY) || getPlatform() === 'mobile-web') {
              next();
            } else {
              router.push('/login');
            }
          } else {
            console.log('Merchant UID missing, redirecting to authCallback');
            router.push('/authcallback');
          }
        } else {
          Auth.Instance.signIn();
        }
      });
  } else {
    next();
  }
});

// Route permission guard: blocks access if user lacks required permission (skip on mobile-web, no staff login)
router.beforeEach((to, from, next) => {
  const requiredPermission = to.meta?.permission as string | undefined;
  if (!requiredPermission || getPlatform() === 'mobile-web' || getPlatform() === 'desktop-web') return next();
  const aclStore = useAcl();
  if (aclStore.hasPermission(requiredPermission)) next();
  else next(from.path === to.path ? '/pos' : from);
});

export default router;
